import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { LeaderboardTable } from "../components/LeaderboardTable";
import { api, ApiError } from "../lib/api";

/**
 * Everybody's best laps, readable without an account.
 *
 * A driver appears here once per track and class, with the quickest valid lap from
 * any session they have not marked private. Two rows can be ticked to put the laps
 * side by side, which is the whole reason a stranger's lap is readable at all.
 */
export function LeaderboardPage() {
  const { t } = useTranslation();
  const [track, setTrack] = useState<string>("");

  const me = useQuery({
    queryKey: ["me"],
    queryFn: api.me,
    retry: (count, error) =>
      !(error instanceof ApiError && error.status === 401) && count < 2,
  });

  const board = useQuery({
    queryKey: ["leaderboard"],
    queryFn: () => api.publicLeaderboard(),
  });

  const tracks = useMemo(
    () => [...new Set((board.data ?? []).map((entry) => entry.track_name))].sort(),
    [board.data],
  );

  const entries = useMemo(
    () =>
      track ? (board.data ?? []).filter((entry) => entry.track_name === track) : (board.data ?? []),
    [board.data, track],
  );

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("leaderboard.title")}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">{t("leaderboard.subtitle")}</p>
        </div>

        {tracks.length > 1 && (
          <select
            value={track}
            onChange={(event) => setTrack(event.target.value)}
            className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-300"
          >
            <option value="">{t("leaderboard.allTracks")}</option>
            {tracks.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        )}
      </div>

      {board.isPending ? (
        <p className="mt-8 text-sm text-zinc-500">{t("leaderboard.loading")}</p>
      ) : board.isError ? (
        <p className="mt-8 text-sm text-red-400">{t("leaderboard.error")}</p>
      ) : entries.length === 0 ? (
        <div className="mt-8 rounded-xl border border-zinc-800 px-6 py-16 text-center">
          <p className="text-sm text-zinc-400">{t("leaderboard.emptyTitle")}</p>
          <p className="mt-1 text-sm text-zinc-600">{t("leaderboard.emptyBody")}</p>
        </div>
      ) : (
        <div className="mt-8">
          <LeaderboardTable
            entries={entries}
            comparePath="/compare"
            currentUserId={me.data?.id}
          />
        </div>
      )}
    </div>
  );
}
